import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import styled from 'styled-components';

import { useAiAgentPermissions } from 'core/hooks/AiAgents/useAiAgentPermissions';
import {
  aiCheckpointNoAccess,
  aiCheckpointStatus,
  AiPortraitCheckpoint,
  aiPortraitCheckpointsOrdered,
  isAiPortraitCheckpointKey,
} from 'core/hooks/AiPortrait/aiPortraitModel';
import { T } from 'services/ThemeTokens';

import { AiCheckpointIcon } from './AiCheckpointIcon';

interface IAiCheckpointStripProps {
  checkpoints: ReadonlyArray<AiPortraitCheckpoint>;
  size?: number;
}

/** Название чекпоинта: известный ключ — фраза, незнакомый — как пришёл. */
export const useCheckpointTitle = (): ((key: string) => string) => {
  const { t } = useTranslation();
  return (key: string) =>
    isAiPortraitCheckpointKey(key) ? t(`aiPortrait.checkpoints.${key}`) : key;
};

/**
 * ТЗ 0120 — ряд значков шести чекпоинтов в фиксированном порядке
 * (мини-карточка коммуникатора, тизер на «Обзоре»). Подсказка — название и
 * короткий вывод модели; то же — имя пункта для чтения с экрана. Портрет v2
 * (чекпоинтов нет) — ничего.
 */
export const AiCheckpointStrip = memo(
  ({ checkpoints, size = 16 }: IAiCheckpointStripProps) => {
    const { t } = useTranslation();
    const titleOf = useCheckpointTitle();
    const { canSeeFinancePortrait } = useAiAgentPermissions();
    const ordered = aiPortraitCheckpointsOrdered(checkpoints);
    if (!ordered.length) return null;
    return (
      <Row role="list" aria-label={t('aiPortrait.checkpointsTitle')}>
        {ordered.map((item) => {
          const title = titleOf(item.key);
          const noAccess = aiCheckpointNoAccess(
            item.key,
            item.status,
            canSeeFinancePortrait,
          );
          const status = noAccess
            ? t('aiPortrait.checkpointStatus.noAccess')
            : t(
                `aiPortrait.checkpointStatus.${aiCheckpointStatus(
                  item.status,
                )}`,
              );
          const tip = noAccess
            ? `${title}: ${t('aiPortrait.checkpointNoAccess')}`
            : item.text
            ? `${title}: ${item.text}`
            : `${title}: ${status}`;
          return (
            <Tooltip key={item.key} title={tip} mouseEnterDelay={0.15}>
              <Cell
                role="listitem"
                aria-label={
                  item.text
                    ? `${title} — ${status}. ${item.text}`
                    : `${title} — ${status}`
                }
              >
                <AiCheckpointIcon
                  status={item.status}
                  size={size}
                  noAccess={noAccess}
                />
              </Cell>
            </Tooltip>
          );
        })}
      </Row>
    );
  },
);

AiCheckpointStrip.displayName = 'AiCheckpointStrip';

const Row = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const Cell = styled.span`
  display: inline-flex;
  padding: 2px;
  border-radius: 6px;
  cursor: help;

  &:hover {
    background: ${T.hover};
  }
`;
